import React from 'react';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import type { ILead } from 'src/services/api';
import { Flex, FlexColumn } from 'src/components/flex';
import { formatSmartField } from 'src/components/lead-sidebar/format-smart-field';
import EditFactButton from 'src/components/lead-sidebar/EditFactButton';
import { useJustAddedKeys } from 'src/components/lead-sidebar/just-added-facts-context';
import { formatIncome, splitSmartFields } from 'src/components/lead-sidebar/displayed-smart-fields';
import styles from 'src/components/lead-sidebar/just-added-highlight.module.css';

const EDITABLE_SMART_FIELD_KEYS = new Set(['pet_type', 'voucher_type', 'household_size']);

const ICON_SMART_FIELD_KEYS = new Set(['income', 'household_size', 'voucher_type']);

function SmartField({
    field: propsField,
    value: propsValue,
    editButton,
    isJustAdded = false,
}: {
    field: string;
    value: string;
    editButton?: React.ReactElement<{ children?: React.ReactNode }>;
    isJustAdded?: boolean;
}) {
    const {
        fieldLabel,
        value,
        valueLabel,
        isValueALink
    } = formatSmartField(propsField, propsValue);

    const labelElem = (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {fieldLabel}:
        </Typography>
    );

    const valueElem = isValueALink ? (
        <Link href={value} target="_blank" rel="noreferrer" noWrap>
            {valueLabel}
        </Link>
    ) : editButton ? (
        <Typography
            variant="body2" noWrap title={value}
            sx={{ cursor: 'pointer', '&:hover': { textShadow: '0 0 0.75px currentColor' } }}
        >
            {value}
        </Typography>
    ) : (
        <Typography variant="body2" noWrap title={value}>{value}</Typography>
    );

    return (
        <FlexColumn rowGap={0} className={isJustAdded ? styles.justAddedHighlight : undefined}>
            <Flex alignItems="center" columnGap={0.5}>
                {labelElem}
                {editButton && !isValueALink ? React.cloneElement(editButton, {}, valueElem) : valueElem}
                {ICON_SMART_FIELD_KEYS.has(propsField) && (
                    <Tooltip title="Extracted with AI">
                        <img src="/img/conversation/box_logo.png" alt="Extracted with AI" width={10} height={10} />
                    </Tooltip>
                )}
            </Flex>
        </FlexColumn>
    );
}

type Props = {
    lead: ILead;
    validSmartFields: [string, string | Record<string, string>][];
}

/**
 * Renders a lead's smart fields as a flat list (no surrounding section/accordion).
 * Used inside the Lead Info section and the smart-fields modal.
 */
export default function SmartFieldsList({ lead, validSmartFields }: Props) {
    const { monthlyIncome, annualIncome, hasIncome, otherFields } = splitSmartFields(validSmartFields);

    const justAddedKeys = useJustAddedKeys();

    /* Render nothing when there are no fields, to avoid an empty layout gap. */
    if (!hasIncome && otherFields.length === 0) {
        return null;
    }

    return (
        <FlexColumn rowGap={1.5}>
            {hasIncome && (
                <SmartField
                    field="income"
                    value={formatIncome(monthlyIncome, annualIncome)}
                    isJustAdded={justAddedKeys.has('income')}
                    editButton={
                        <EditFactButton
                            leadUuid={lead.uuid}
                            title="Income"
                            testId="income"
                            fields={[
                                { key: 'monthly_income', label: 'Monthly', value: monthlyIncome ?? '' },
                                { key: 'annual_income', label: 'Annual', value: annualIncome ?? '' },
                            ]}
                        />
                    }
                />
            )}

            {otherFields.map(([key, value]) => (
                <SmartField
                    key={key}
                    field={key}
                    value={value}
                    isJustAdded={justAddedKeys.has(key)}
                    editButton={
                        EDITABLE_SMART_FIELD_KEYS.has(key) ?
                            (
                                <EditFactButton
                                    leadUuid={lead.uuid}
                                    title={formatSmartField(key, value).fieldLabel}
                                    testId={key}
                                    fields={[{ key, value }]}
                                />
                            ) :
                            undefined
                    }
                />
            ))}
        </FlexColumn>
    );
}
